import React from 'react';
import { TrendingUp, TrendingDown, Minus } from 'lucide-react';
import { BarChart, Bar, ResponsiveContainer } from 'recharts';

interface SeriesMeta {
  key:   string;
  name:  string;
  color: string;
}

interface EnrichedTooltipProps {
  active?:    boolean;
  payload?:   { name: string; value: number; color: string; dataKey: string }[];
  label?:     string;
  series:     SeriesMeta[];
  allData:    Record<string, unknown>[];
  xDataKey:   string;
}

/**
 * Replaces the default Recharts tooltip with an enriched card showing:
 * - Current values per series
 * - Delta vs previous data point (% change)
 * - A mini sparkline of the primary series for context
 */
export default function EnrichedTooltip({
  active, payload, label, series, allData, xDataKey,
}: EnrichedTooltipProps) {
  if (!active || !payload?.length) return null;

  // Find index of current x-label in allData
  const idx = allData.findIndex((d) => String(d[xDataKey]) === String(label));

  return (
    <div style={{
      background: '#fff', border: '1px solid rgba(0,0,0,0.1)',
      borderRadius: 10, padding: '10px 14px',
      boxShadow: '0 8px 24px rgba(0,0,0,0.10)',
      minWidth: 180, maxWidth: 260,
    }}>
      {/* Header */}
      <p style={{ fontSize: 11, fontWeight: 700, color: '#1a1a2e', marginBottom: 8 }}>
        {label}
      </p>

      {/* Series rows */}
      {series.map((s) => {
        const current = payload.find((p) => p.dataKey === s.key)?.value ?? 0;
        const prev    = idx > 0 ? Number(allData[idx - 1]?.[s.key] ?? 0) : null;
        const delta   = prev !== null && prev !== 0 ? ((current - prev) / prev) * 100 : null;
        const dir     = delta === null ? 'flat' : delta > 0 ? 'up' : delta < 0 ? 'down' : 'flat';
        const DeltaIcon = dir === 'up' ? TrendingUp : dir === 'down' ? TrendingDown : Minus;
        const deltaColor = dir === 'up' ? '#16A34A' : dir === 'down' ? '#DC2626' : '#94A3B8';

        // Sparkline for this series
        const sparkData = allData.slice(Math.max(0, idx - 5), idx + 1).map((d) => ({
          v: Number(d[s.key] ?? 0),
        }));

        return (
          <div key={s.key} style={{ marginBottom: 8 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 3 }}>
              <div style={{ width: 8, height: 8, borderRadius: 2, background: s.color, flexShrink: 0 }} />
              <span style={{ fontSize: 10.5, color: '#5a5a6e', flex: 1 }}>{s.name}</span>
              <span style={{ fontSize: 13, fontWeight: 700, color: '#1a1a2e' }}>
                {current.toLocaleString()}
              </span>
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              {/* Delta badge */}
              {delta !== null && (
                <div style={{
                  display: 'flex', alignItems: 'center', gap: 2,
                  padding: '1px 5px', borderRadius: 100,
                  background: dir === 'flat' ? 'rgba(0,0,0,0.04)' : dir === 'up' ? '#F0FDF4' : '#FEF2F2',
                }}>
                  <DeltaIcon size={9} color={deltaColor} />
                  <span style={{ fontSize: 9.5, fontWeight: 600, color: deltaColor }}>
                    {delta > 0 ? '+' : ''}{delta.toFixed(1)}% vs prev
                  </span>
                </div>
              )}

              {/* Mini sparkline */}
              {sparkData.length > 1 && (
                <div style={{ width: 56, height: 20, marginLeft: 'auto' }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={sparkData} barSize={5} margin={{ top: 0, right: 0, bottom: 0, left: 0 }}>
                      <Bar dataKey="v" fill={s.color} opacity={0.6} radius={[1, 1, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}
            </div>
          </div>
        );
      })}

      {/* Total row when multiple series */}
      {series.length > 1 && (
        <div style={{
          borderTop: '1px solid rgba(0,0,0,0.04)', marginTop: 4, paddingTop: 6,
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        }}>
          <span style={{ fontSize: 10, color: '#94A3B8', fontWeight: 600 }}>TOTAL</span>
          <span style={{ fontSize: 12, fontWeight: 700, color: '#1a1a2e' }}>
            {payload.reduce((a, p) => a + (Number(p.value) || 0), 0).toLocaleString()}
          </span>
        </div>
      )}
    </div>
  );
}
